import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RotateCcw, Trash2 } from "lucide-react";
import { ApprovalPost } from "@/types/approval";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface DeletedApprovalsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRestore: () => void;
}

export function DeletedApprovalsDialog({ 
  isOpen, 
  onClose, 
  onRestore 
}: DeletedApprovalsDialogProps) {
  const [deletedApprovals, setDeletedApprovals] = useState<ApprovalPost[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadDeletedApprovals();
    }
  }, [isOpen]);

  const loadDeletedApprovals = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let targetCompanyId: string | null = null;

      // Verificar se é colaborador
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (collaboratorData) {
        targetCompanyId = collaboratorData.company_id;
      } else {
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        targetCompanyId = userData?.company_id || null;
      }

      if (!targetCompanyId) return;

      const { data, error } = await supabase
        .from('approval_posts')
        .select('*')
        .eq('company_id', targetCompanyId)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });

      if (error) throw error;

      const transformedApprovals: ApprovalPost[] = (data || []).map(approval => ({
        ...approval,
        status: approval.status as 'pending' | 'approved' | 'rejected' | 'archived',
        post_type: (approval.post_type || 'static') as 'static' | 'carousel' | 'reels',
        visible_to: Array.isArray(approval.visible_to) ? approval.visible_to.map(id => String(id)) : []
      }));

      setDeletedApprovals(transformedApprovals);
    } catch (error) {
      console.error('Erro ao carregar aprovações excluídas:', error);
      toast.error('Erro ao carregar aprovações excluídas');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const { error } = await supabase
        .from('approval_posts')
        .update({ deleted_at: null })
        .eq('id', id);

      if (error) throw error;

      toast.success('Aprovação restaurada com sucesso!');
      loadDeletedApprovals();
      onRestore();
    } catch (error) {
      console.error('Erro ao restaurar aprovação:', error);
      toast.error('Erro ao restaurar aprovação');
    }
  };

  const handlePermanentDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir permanentemente esta aprovação? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('approval_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Aprovação excluída permanentemente!');
      loadDeletedApprovals();
    } catch (error) {
      console.error('Erro ao excluir aprovação permanentemente:', error);
      toast.error('Erro ao excluir aprovação');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500 text-white';
      case 'pending':
        return 'bg-yellow-500 text-black';
      case 'rejected':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Aprovações Excluídas</DialogTitle>
        </DialogHeader>
        
        <div className="max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : deletedApprovals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhuma aprovação excluída encontrada.
            </div>
          ) : (
            <div className="grid gap-4">
              {deletedApprovals.map((approval) => (
                <div key={approval.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium">{approval.title}</h4>
                        <Badge className={getStatusColor(approval.status)}>
                          {approval.status.toUpperCase()}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-2">
                        <div>
                          <span className="font-medium">Cliente:</span> {approval.client_name}
                        </div>
                        <div>
                          <span className="font-medium">Plataforma:</span> {approval.platform}
                        </div>
                        <div>
                          <span className="font-medium">Data de postagem:</span>{' '}
                          {approval.post_date ? format(new Date(approval.post_date), 'dd/MM/yyyy', { locale: ptBR }) : 'Não definida'}
                        </div>
                        <div>
                          <span className="font-medium">Excluída em:</span>{' '}
                          {format(new Date(approval.deleted_at!), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </div>
                      </div>
                      
                      {approval.caption && (
                        <div className="text-sm">
                          <span className="font-medium">Legenda:</span>
                          <p className="mt-1 text-gray-600 line-clamp-2">{approval.caption}</p>
                        </div>
                      )}
                    </div>
                    
                    {approval.image_url && (
                      <div className="ml-4">
                        <img
                          src={approval.image_url}
                          alt={approval.title}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-4 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(approval.id)}
                      className="flex items-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Restaurar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handlePermanentDelete(approval.id)}
                      className="flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Excluir Permanentemente
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
